import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { arrayMove } from '@dnd-kit/sortable';
import { validateDecimal } from "../../../../../../../../Apis/functions";
import HandleChange from "../../../../../../../../Helpers/HandleChange";
import { ALL_TIME_END, ALL_TIME_NAME, ALL_TIME_START, POLICY_MODEL, RULE_MODEL } from "../Model/Model";
import { START_TIME } from "../../../../../Employees/Model/Models";
import routes from "../../../../../../../../Routes/routes";
import {toast} from "react-toastify";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * Shared logic hook for both create and update overtime policy flows.
 * Manages stepper state, policy form model, and per-rule validation arrays.
 * @returns {Object} State, setters, and handlers consumed by Create/UpdatePolicyApp.
 */
const CommonPolicyApp = () => {
    const location = useLocation();
    const state = location.state;
    const isGlobal = state?.isGlobal ?? true;
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [emptyFields, setEmptyFields] = useState(false);
    const [item, setItem] = useState({ ...POLICY_MODEL, isGlobal });
    const { handleChange } = HandleChange({ model: item, setModel: setItem });
    const [departments, setDepartments] = useState([]);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [cleanChipSearch, setCleanChipSearch] = useState(false);

    const [rule, setRule] = useState({ ...RULE_MODEL });
    const [ruleEmptyFields, setRuleEmptyFields] = useState(false);
    const [ruleIsValid, setRuleIsValid] = useState(true);
    const [ruleIsValidCalculationValue, setRuleIsValidCalculationValue] = useState(true);
    const [ruleIsValidCalculationValueText, setRuleIsValidCalculationValueText] = useState('this_value_must_be_greater_than_0');

    /**
     * Returns whether the scope fields of a rule at the given index are valid.
     * @param {number} index - Index of the rule within item.rules.
     * @returns {boolean}
     */
    const isValidScope = (index) => item.rules[index]?.isValidScope() ?? true;

    /**
     * Returns whether the calculation value of a rule at the given index passes decimal validation.
     * @param {number} index - Index of the rule within item.rules.
     * @returns {boolean}
     */
    const isValidCalculationValue = (index) => validateDecimal(item.rules[index]?.calculationValue).isValid ?? true;

    /** Returns true when the current rule form (single rule) is fully valid. */
    const isRuleFormValid = () => rule.isValid(isGlobal) && ruleIsValid && ruleIsValidCalculationValue;

    /** Returns true when all rules in the policy list pass field, scope, and calculation-value validation. */
    const isRulesListValid = () =>
        !!item.rules?.length &&
        item.rules.every((_, i) => isValidScope(i)) &&
        item.rules.every((_, i) => isValidCalculationValue(i)) &&
        item.rules.every(r => r?.isValid?.(isGlobal));

    /**
     * Advances the stepper to the summary step after validating the rule form (global)
     * or the full rules list (department).
     */
    const handleNextSummary = () => {
        if (isGlobal) {
            if (!isRuleFormValid()) {
                setRuleEmptyFields(true);
                return;
            }
            setItem(prev => ({ ...prev, rules: [{ ...rule }] }));
            setRuleEmptyFields(false);
            setEmptyFields(false);
            setActiveStep((prev) => prev + 1);
            return;
        }
        if (!isRulesListValid()) {
            setRuleEmptyFields(true);
            if(item.rules?.length === 0){
                toast.warning(t("add_at_least_one_rule"));
            }
            return;
        }
        setRuleEmptyFields(false);
        setActiveStep((prev) => prev + 1);
    };

    /**
     * Re-validates the rule form's scope and calculation value whenever the rule model changes.
     */
    useEffect(() => {
        setRuleIsValid(rule.isValidScope());
        const { isValid: calcValid, text: calcText } = validateDecimal(rule.calculationValue);
        setRuleIsValidCalculationValue(calcValid);
        if (calcText) setRuleIsValidCalculationValueText(calcText);
    }, [rule]);

    /**
     * Initializes the stepper labels after translations are ready.
     * useLayoutEffect avoids a visible flash of untranslated step names.
     */
    useLayoutEffect(() => {
        setSteps([t('details'), t('rules'), t('summary')]);
    }, [t]);

    /** Sets the active stepper step directly. */
    const handleStepChange = (step) => setActiveStep(step);

    /**
     * Appends a new rule to the policy's rule list.
     * @param {Object} newRule - Rule object to add.
     */
    const addRule = (newRule) => {
        setItem((prev) => ({
            ...prev,
            rules: [...prev.rules, { ...newRule }],
        }));
    };

    /**
     * Validates the current rule form and, if valid, appends it to the list and resets the form.
     */
    const handleAddRule = () => {
        if (!rule.isValid(isGlobal) || !ruleIsValid || !ruleIsValidCalculationValue) {
            setRuleEmptyFields(true);
            return;
        }
        addRule(rule);
        setRule({ ...RULE_MODEL });
        setRuleEmptyFields(false);
    };

    const handleDeleteRule = (index) => {
        setSelectedItem(index);
        setShowDeleteModal(true);
    };

    /**
     * Removes a rule immediately by index, without a confirmation step.
     * Used in the create flow where rules are not yet persisted.
     * @param {number} index - Index of the rule within item.rules.
     */
    const removeRule = (index) => {
        setItem(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== index) }));
    };

    const confirmDeleteRule = () => {
        setItem(prev => ({ ...prev, rules: prev.rules.filter((_, i) => i !== selectedItem) }));
        setShowDeleteModal(false);
        setSelectedItem(null);
    };

    /**
     * Reorders the rules list after a drag-and-drop event.
     * @param {Object} event - DndKit drag end event with active and over descriptors.
     */
    const handleReorderRules = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        setItem(prev => {
            const oldIndex = prev.rules.findIndex((r, i) => (r.id ?? i) === active.id);
            const newIndex = prev.rules.findIndex((r, i) => (r.id ?? i) === over.id);
            return { ...prev, rules: arrayMove(prev.rules, oldIndex, newIndex) };
        });
    };

    /**
     * Updates a single field of the current rule form, applying allTime side-effects when needed.
     * @param {string} field - Field name to update.
     * @param {*} value - New value for the field.
     */
    const updateRuleField = (field, value) => {
        if (field === ALL_TIME_NAME) {
            setRule(prev => ({ ...prev, [field]: value, startTime: ALL_TIME_START, endTime: value ? ALL_TIME_END : START_TIME }));
            return;
        }
        setRule(prev => ({ ...prev, [field]: value }));
    };

    /**
     * Navigates back to the previous screen using the backUrl from navigation state.
     */
    const goBack = () => {
        if (state?.backUrl) {
            navigate(state.backUrl, { state: { ...state } });
        } else {
            navigate(routes.attendance.main);
        }
    };

    return {
        t,
        navigate,
        isGlobal,
        item,
        setItem,
        activeStep,
        setActiveStep,
        steps,
        emptyFields,
        setEmptyFields,
        handleChange,
        departments,
        setDepartments,
        handleReorderRules,
        handleNextSummary,
        handleStepChange,
        goBack,
        rule,
        setRule,
        updateRuleField,
        ruleEmptyFields,
        setRuleEmptyFields,
        ruleIsValid,
        ruleIsValidCalculationValue,
        ruleIsValidCalculationValueText,
        handleAddRule,
        handleDeleteRule,
        removeRule,
        confirmDeleteRule,
        showDeleteModal,
        setShowDeleteModal,
        selectedItem,
        setSelectedItem,
        cleanChipSearch,
        setCleanChipSearch,
        isRuleFormValid
    };
};

export default CommonPolicyApp;
